import { RootStateType } from "@Interfaces";
import {
    createSlice,
    createAsyncThunk,
    CaseReducer,
    PayloadAction,
} from "@reduxjs/toolkit";
import postAutocompletion from "@Services/API/Completions";
import { actions as notificationActions } from "@Store/Reducers/notifications";

export enum CompletionsRequestState {
    IDLE = "idle",
    LOADING = "loading",
    SUCCEDEED = "succeeded",
    FAILED = "failed",
}

type CompletionsState = {
    completions: string[];
    question: string;
    status:
        | CompletionsRequestState.IDLE
        | CompletionsRequestState.FAILED
        | CompletionsRequestState.SUCCEDEED
        | CompletionsRequestState.LOADING;
    error: string | null;
};

const initialState: CompletionsState = {
    question: "",
    completions: [],
    status: CompletionsRequestState.IDLE,
    error: null,
};

const isAnotherQuestion = (previous: string, current: string): boolean => {
    return (
        previous.replace(/\s+/g, "").toLowerCase() !==
        current.replace(/\s+/g, "").toLowerCase()
    );
};

export const fetchAutocompletions = createAsyncThunk(
    "completion/fetchAutocompletions",
    async (question: string, { getState, dispatch }) => {
        const { completions } = getState() as RootStateType;

        if (isAnotherQuestion(question, completions.question)) {
            const response = await postAutocompletion({
                question,
            });

            if (!response.length) {
                // if not results were found
                dispatch(
                    notificationActions.notification({
                        snackbarOpen: true,
                        snackbarType: "error",
                        snackbarMessage:
                            "Suggestions not found, try with a new prompt",
                    })
                );
            }

            return response;
        } else {
            return [...completions.completions].reverse(); // we re-use the cached values
        }
    }
);

export const selectAllCompletion = (state: any) => {
    return state.completions?.completions;
};

export const selectCompletionStatus = (state: any) => {
    return state.completions?.status;
};

const setQuestion: CaseReducer<CompletionsState, PayloadAction<any>> = (
    state,
    { payload }
) => {
    state.question = payload;
};

const completionsSlice = createSlice({
    name: "completions",
    initialState,
    reducers: {
        setQuestion,
    },
    extraReducers(builder) {
        builder
            .addCase(fetchAutocompletions.pending, (state, action) => {
                state.status = CompletionsRequestState.LOADING;
            })
            .addCase(fetchAutocompletions.fulfilled, (state, action) => {
                state.status = CompletionsRequestState.SUCCEDEED;
                state.completions = action.payload;
            })
            .addCase(
                fetchAutocompletions.rejected,
                (state: CompletionsState, action) => {
                    state.status = CompletionsRequestState.FAILED;
                }
            );
    },
});

export const { name, actions } = completionsSlice;

export default completionsSlice.reducer;
